import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { toast } from 'sonner'
import type { ChatImage, DocumentReference } from '@/components/layout/chat-pane.types'
import type { ChatSessionExporter, ChatSessionSnapshot } from '@/lib/chat-session-snapshot'
import { focusChatComposer, collapseChatAgentPanel, refreshChatsSidebar } from '@/lib/chats-sidebar-events'
import { OPEN_INGESTION_IN_AGENT_PANEL, type OpenIngestionInAgentPanelDetail } from '@/lib/open-ingestion-conversation'
import { StorageKeys } from '@/lib/storage/keys'
import { canRestoreAgentPanelThread } from '@/lib/chat-conversation-scope'
import {
  markPromotedFromNewThread,
  rekeyHeldUserTurn,
} from '@/components/layout/chat-pane.utils'
import { peekThreadWorkspaceMeta, rememberThreadWorkspaceMeta } from '@/lib/thread-workspace-meta'
import { getThread } from '@/lib/api/threads'
import {
  clearAgentPanelLastThreadId,
  persistAgentPanelLastThreadId,
  persistAgentPanelLastThreadMeta,
  readAgentPanelLastThreadId,
  readAgentPanelLastThreadMeta,
} from '@/lib/agent-panel-last-thread'
import {
  clearAgentPanelAutoRestoreSuppress,
  shouldBlockSuppressedAutoRestore,
  suppressAgentPanelAutoRestore,
} from '@/lib/agent-panel-auto-restore-suppress'

export type ChatSurface = 'editor' | 'agent-panel'

export type ConversationDraft = {
  text: string
  references: DocumentReference[]
  images: ChatImage[]
  /** ID of the document tab that was active when this new chat was created (shown as dismissable chip). */
  contextDocumentId?: string
  /** Title of the context document (for display without an extra API round-trip). */
  contextDocumentTitle?: string
}

/** True when a blank new-chat draft has no user-entered content (welcome / empty composer). */
export function isEmptyConversationDraft(draft: ConversationDraft | null | undefined): boolean {
  if (!draft) return true
  if (typeof draft.text === 'string' && draft.text.trim().length > 0) return false
  if (Array.isArray(draft.references) && draft.references.length > 0) return false
  if (Array.isArray(draft.images) && draft.images.length > 0) return false
  return true
}

/**
 * Composer / panel focus must not call restore when any thread is already hosted
 * (including intentional `new-*`). Restore-on-focus for blanks stole the panel back to
 * sessionStorage last-thread (e.g. Weekly Todo Triage) - see incident 2026-07-23.
 * Empty panel (`null`) still restores on focus / mount.
 */
export function shouldRestoreAgentPanelOnFocus(
  agentThreadId: string | null | undefined,
): boolean {
  return !agentThreadId
}

/**
 * Focus-capture restore. Skip when the user is using composer chrome
 * (Ask/Plan/Agent picker, typed text, chips) - that path stole the panel
 * back to sessionStorage last-thread (Flow agent / Triage). See 2026-09-01.
 */
export function shouldRestoreAgentPanelOnComposerFocus(
  agentThreadId: string | null | undefined,
  focusTarget: EventTarget | null,
): boolean {
  if (!shouldRestoreAgentPanelOnFocus(agentThreadId)) return false
  if (!(focusTarget instanceof Element)) return true
  if (focusTarget.closest('[data-testid="chat-mode-selector"]')) return false
  if (focusTarget.closest('[role="menu"]')) return false
  if (focusTarget.closest('[data-radix-popper-content-wrapper]')) return false
  const editor =
    focusTarget.closest('[contenteditable="true"]') ||
    focusTarget.closest('[data-testid="chat-pane-input"]')
  if (editor) {
    const text = (editor.textContent || '').trim()
    const hasChip = Boolean(editor.querySelector('[data-reference-type="chip"]'))
    if (text || hasChip) return false
  }
  return true
}

export type RestoreLastAgentPanelOptions = {
  /** When false, only return an already-hosted real thread (welcome-composer typing). */
  allowSwitch?: boolean
  /** Sidebar active org ids. Out-of-scope last-thread is not restored. */
  activeWorkspaceIds?: string[]
}

/** Opt-in verbose chat-surface logs: `localStorage.setItem('flownotesDebugChatSurface', '1')`. */
export function isChatSurfaceDebugEnabled(): boolean {
  if (import.meta.env.DEV) return true
  try {
    return localStorage.getItem('flownotesDebugChatSurface') === '1'
  } catch {
    return false
  }
}

export type ChatSurfaceRestoreLog = {
  reason: string
  from: string | null
  to: string | null
  stored: string | null
  outcome: 'switched' | 'keep-current' | 'keep-blank' | 'noop'
  detail?: string
}

/** Always logs real thread switches; no-ops only when debug flag / DEV. */
export function logChatSurfaceRestore(entry: ChatSurfaceRestoreLog): void {
  const switched = entry.outcome === 'switched'
  if (!switched && !isChatSurfaceDebugEnabled()) return
  const payload = {
    reason: entry.reason,
    from: entry.from,
    to: entry.to,
    stored: entry.stored,
    outcome: entry.outcome,
    ...(entry.detail ? { detail: entry.detail } : {}),
  }
  if (switched) {
    console.info('[ChatSurface] restoreLastAgentPanelThread', payload)
  } else {
    console.debug('[ChatSurface] restoreLastAgentPanelThread', payload)
  }
}

export type EditorChatTab = {
  tabId: string
  threadId: string
  title: string
  isPinned?: boolean
}

export type OpenChatBinding = {
  threadId: string
  surface: ChatSurface
  tabId?: string
}

export type OpenConversationOptions = {
  forceAgentPanel?: boolean
  openInNewTab?: boolean
  /** When set, open-intent is resolved in the tab handler (honors pinned / home / integration). */
  clickEvent?: import('@/lib/open-intent').OpenIntentClickEvent
  forceEditor?: boolean
  title?: string
}

/** Build OpenTab id for a chat-conversation tab from threadId. */
export function chatConversationTabId(threadId: string): string {
  return `chat-conversation-${threadId}`
}

/** True when the main editor tab is a chat-conversation host (not a document). */
export function isChatConversationEditorTabId(tabId: string | null | undefined): boolean {
  return Boolean(tabId?.startsWith('chat-conversation-'))
}

/** Prefer agent panel when a document (or non-chat tab) is active in the main editor. */
export function shouldPreferAgentPanelForChatOpen(
  activeEditorTabId: string | null | undefined,
): boolean {
  if (!activeEditorTabId || activeEditorTabId === 'home') return false
  return !isChatConversationEditorTabId(activeEditorTabId)
}

function persistLastAgentPanelThreadId(threadId: string | null | undefined): void {
  persistAgentPanelLastThreadId(threadId)
  if (!threadId || threadId.startsWith('new-')) return
  const meta = peekThreadWorkspaceMeta(threadId)
  if (meta) {
    persistAgentPanelLastThreadMeta({
      threadId: meta.threadId,
      taggedOrganizationIds: meta.taggedOrganizationIds,
      workspaceId: meta.workspaceId,
    })
  }
}

function readLastAgentPanelThreadId(): string | null {
  return readAgentPanelLastThreadId()
}

function hydrateLastAgentPanelThreadMeta(): void {
  const stored = readAgentPanelLastThreadMeta()
  if (!stored) return
  rememberThreadWorkspaceMeta({
    id: stored.threadId,
    taggedOrganizationIds: stored.taggedOrganizationIds,
    workspaceId: stored.workspaceId,
    organizationId: stored.organizationId ?? null,
  })
}

function clearLastAgentPanelThreadId(threadId?: string): void {
  clearAgentPanelLastThreadId(threadId)
}

export { isModClick, type OpenIntentClickEvent } from '@/lib/open-intent'

/** Read persisted open-chats-in-editor preference from localStorage. */
function readOpenInEditorPref(): boolean {
  try {
    const raw = localStorage.getItem(StorageKeys.AGENTS_OPEN_IN_EDITOR)
    if (raw === null) return true
    return raw === 'true'
  } catch {
    return true
  }
}

/** Persist open-chats-in-editor preference to localStorage. */
function writeOpenInEditorPref(value: boolean): void {
  try {
    localStorage.setItem(StorageKeys.AGENTS_OPEN_IN_EDITOR, String(value))
  } catch {
    /* ignore */
  }
}

/** Fallback display title for a thread id. */
function defaultTitleForThread(threadId: string): string {
  if (threadId.startsWith('new-')) return 'New chat'
  return 'Chat'
}

export type ChatSurfaceRegistryValue = ReturnType<typeof useChatSurfaceRegistryState>

const ChatSurfaceRegistryContext = createContext<ChatSurfaceRegistryValue | null>(null)

/** React context provider for shared chat surface registry state. */
export function ChatSurfaceRegistryProvider({ children }: { children: ReactNode }) {
  const value = useChatSurfaceRegistryState()
  return (
    <ChatSurfaceRegistryContext.Provider value={value}>
      {children}
    </ChatSurfaceRegistryContext.Provider>
  )
}

/** Access chat surface registry; throws if outside provider. */
export function useChatSurfaceRegistry(): ChatSurfaceRegistryValue {
  const ctx = useContext(ChatSurfaceRegistryContext)
  if (!ctx) {
    throw new Error('useChatSurfaceRegistry must be used within ChatSurfaceRegistryProvider')
  }
  return ctx
}

/** Access chat surface registry or null when provider is absent. */
export function useChatSurfaceRegistryOptional(): ChatSurfaceRegistryValue | null {
  return useContext(ChatSurfaceRegistryContext)
}

/** Internal hook implementing chat surface registry state and actions. */
function useChatSurfaceRegistryState() {
  const [openInEditor, setOpenInEditorState] = useState(readOpenInEditorPref)
  const [agentPanelThreadId, setAgentPanelThreadId] = useState<string | null>(null)
  const [agentPanelExpanded, setAgentPanelExpanded] = useState(true)
  const [drafts, setDrafts] = useState<Record<string, ConversationDraft>>({})
  const [bindings, setBindings] = useState<Record<string, OpenChatBinding>>({})
  const [threadTitles, setThreadTitles] = useState<Record<string, string>>({})
  const [activeNewChatId, setActiveNewChatId] = useState<string | null>(null)
  const [streamingConversationId, setStreamingConversationId] = useState<string | null>(null)
  const [streamingThreadId, setStreamingThreadId] = useState<string | null>(null)
  const [focusedThreadId, setFocusedThreadId] = useState<string | null>(null)
  const [lastAction, setLastAction] = useState('')

  const bindingsRef = useRef(bindings)
  bindingsRef.current = bindings
  const activeNewChatIdRef = useRef(activeNewChatId)
  activeNewChatIdRef.current = activeNewChatId
  const threadTitlesRef = useRef(threadTitles)
  threadTitlesRef.current = threadTitles

  const agentPanelThreadIdRef = useRef(agentPanelThreadId)
  agentPanelThreadIdRef.current = agentPanelThreadId

  const ingestionBootstrapThreadIdRef = useRef<string | null>(null)
  const restoreLookupThreadIdRef = useRef<string | null>(null)
  const restoreLastAgentPanelThreadRef = useRef<
    (reason?: string, options?: RestoreLastAgentPanelOptions) => string | null
  >(() => null)
  const sessionSnapshotsRef = useRef<Record<string, ChatSessionSnapshot>>({})
  const sessionExportersRef = useRef<Record<string, ChatSessionExporter>>({})

  /** Mark ingestion bootstrap in flight so restoreLastAgentPanelThread does not steal focus. */
  const endIngestionBootstrap = useCallback((threadId?: string | null) => {
    if (!threadId || ingestionBootstrapThreadIdRef.current === threadId) {
      ingestionBootstrapThreadIdRef.current = null
    }
  }, [])

  /** Resolve display title for a thread from registry cache or defaults. */
  const resolveTitle = useCallback(
    (threadId: string) => threadTitlesRef.current[threadId] ?? defaultTitleForThread(threadId),
    [],
  )

  /** In-memory blank chat id (new-*) until first message or history load. */
  const getBlankNewChatId = useCallback((): string | null => {
    const panelId = agentPanelThreadIdRef.current
    if (panelId?.startsWith('new-')) {
      return panelId
    }
    // Agent panel hosts a persisted thread: never reuse stale blank drafts.
    if (panelId) {
      return null
    }
    if (activeNewChatIdRef.current?.startsWith('new-')) {
      return activeNewChatIdRef.current
    }
    for (const threadId of Object.keys(bindingsRef.current)) {
      if (
        threadId.startsWith('new-')
        && bindingsRef.current[threadId]?.surface === 'agent-panel'
      ) {
        return threadId
      }
    }
    return null
  }, [])

  /** Update open-chats-in-editor preference and persist it. */
  const setOpenInEditor = useCallback((value: boolean) => {
    writeOpenInEditorPref(value)
    setOpenInEditorState(value)
    setLastAction(value ? 'Preference: open chats in editor' : 'Preference: open chats in agent panel')
  }, [])

  /** Cache a thread title for tab and sidebar display. */
  const registerThreadTitle = useCallback((threadId: string, title: string) => {
    setThreadTitles((prev) => ({ ...prev, [threadId]: title }))
  }, [])

  /** Get in-session composer draft for a thread. */
  const getDraft = useCallback(
    (threadId: string): ConversationDraft =>
      drafts[threadId] ?? { text: '', references: [], images: [] },
    [drafts],
  )

  /** Store in-session composer draft for a thread. */
  const setDraft = useCallback((threadId: string, draft: ConversationDraft) => {
    setDrafts((prev) => ({ ...prev, [threadId]: draft }))
  }, [])

  /** Get which surface (editor or agent panel) hosts a thread. */
  const getBinding = useCallback(
    (threadId: string): OpenChatBinding | undefined => bindings[threadId],
    [bindings],
  )

  /** True when thread is open in editor tab or is the current agent-panel host. */
  const isThreadOpen = useCallback(
    (threadId: string): boolean => {
      if (agentPanelThreadId === threadId) return true
      const binding = bindings[threadId]
      return binding?.surface === 'editor' && Boolean(binding.tabId)
    },
    [agentPanelThreadId, bindings],
  )

  /** True when thread is the focused chat surface (agent panel or active editor chat tab). */
  const isThreadActive = useCallback(
    (threadId: string, activeEditorTabId?: string | null, chatPaneFocusedThreadId?: string | null): boolean => {
      if (activeEditorTabId === chatConversationTabId(threadId)) return true
      if (agentPanelThreadId === threadId && chatPaneFocusedThreadId === threadId) return true
      return false
    },
    [agentPanelThreadId],
  )

  /** Record that a thread is hosted on a surface. */
  const bindThread = useCallback((binding: OpenChatBinding) => {
    setBindings((prev) => ({ ...prev, [binding.threadId]: binding }))
    bindingsRef.current = { ...bindingsRef.current, [binding.threadId]: binding }
  }, [])

  /** Remove surface binding for a thread. */
  const unbindThread = useCallback((threadId: string) => {
    setBindings((prev) => {
      const next = { ...prev }
      delete next[threadId]
      return next
    })
    const next = { ...bindingsRef.current }
    delete next[threadId]
    bindingsRef.current = next
    setFocusedThreadId((current) => (current === threadId ? null : current))
  }, [])

  /** Clear agent panel selection when it matches threadId. */
  const clearAgentPanelIfThread = useCallback((threadId: string) => {
    setAgentPanelThreadId((current) => {
      if (current === threadId) {
        agentPanelThreadIdRef.current = null
        return null
      }
      return current
    })
  }, [])

  /** Show agent panel and select the given thread. */
  const focusAgentPanel = useCallback(
    (threadId: string) => {
      agentPanelThreadIdRef.current = threadId
      setAgentPanelThreadId(threadId)
      setAgentPanelExpanded(true)
      persistLastAgentPanelThreadId(threadId)
      setLastAction(`Focused agent panel: ${resolveTitle(threadId)}`)
    },
    [resolveTitle],
  )

  /** Register a function that captures live ChatPane state before surface moves. */
  const setSessionExporter = useCallback((threadId: string, exporter: ChatSessionExporter) => {
    sessionExportersRef.current[threadId] = exporter
  }, [])

  const clearSessionExporter = useCallback((threadId: string) => {
    delete sessionExportersRef.current[threadId]
  }, [])

  const captureSessionSnapshot = useCallback((threadId: string) => {
    const exporter = sessionExportersRef.current[threadId]
    if (exporter) {
      sessionSnapshotsRef.current[threadId] = exporter()
    }
  }, [])

  const takeSessionSnapshot = useCallback((threadId: string): ChatSessionSnapshot | null => {
    const snap = sessionSnapshotsRef.current[threadId]
    if (snap) {
      delete sessionSnapshotsRef.current[threadId]
    }
    return snap ?? null
  }, [])

  /** Open or focus a chat-conversation editor tab for threadId. */
  const openInEditorTab = useCallback(
    (threadId: string, options?: Pick<OpenConversationOptions, 'openInNewTab' | 'clickEvent' | 'title'>) => {
      captureSessionSnapshot(threadId)

      const existing = bindingsRef.current[threadId]
      if (existing?.surface === 'editor' && existing.tabId) {
        window.dispatchEvent(
          new CustomEvent('focus-chat-conversation-main', { detail: { threadId } }),
        )
        focusChatComposer(threadId)
        setLastAction(`Focused existing tab: ${resolveTitle(threadId)}`)
        return
      }

      const hostedOnAgent =
        existing?.surface === 'agent-panel' ||
        agentPanelThreadIdRef.current === threadId

      if (existing?.surface === 'agent-panel') {
        clearAgentPanelIfThread(threadId)
        unbindThread(threadId)
      } else if (agentPanelThreadIdRef.current === threadId) {
        agentPanelThreadIdRef.current = null
        setAgentPanelThreadId(null)
        unbindThread(threadId)
      }

      const tabId = chatConversationTabId(threadId)
      const title = options?.title ?? resolveTitle(threadId)
      bindThread({ threadId, surface: 'editor', tabId })
      window.dispatchEvent(
        new CustomEvent('open-chat-conversation-main', {
          detail: {
            threadId,
            title,
            openInNewTab: options?.openInNewTab,
            clickEvent: options?.clickEvent,
          },
        }),
      )
      if (hostedOnAgent) {
        collapseChatAgentPanel()
      }
      toast.success('Chat opened in editor tab', { description: title })
      setLastAction(
        options?.openInNewTab
          ? `New editor tab (Cmd/Ctrl+click): ${title}`
          : `Opened in editor tab: ${title}`,
      )
    },
    [bindThread, clearAgentPanelIfThread, resolveTitle, unbindThread, captureSessionSnapshot],
  )

  /** Open or focus agent panel for threadId, closing editor host if needed. */
  const openInAgentPanel = useCallback(
    (threadId: string) => {
      captureSessionSnapshot(threadId)
      if (!threadId.startsWith('new-')) {
        setActiveNewChatId(null)
        activeNewChatIdRef.current = null
      }

      const existing = bindingsRef.current[threadId]
      if (existing?.surface === 'agent-panel' && agentPanelThreadIdRef.current === threadId) {
        focusAgentPanel(threadId)
        return
      }

      if (existing?.surface === 'editor' && existing.tabId) {
        window.dispatchEvent(
          new CustomEvent('close-chat-conversation-tab', { detail: { threadId } }),
        )
        unbindThread(threadId)
      }

      const prevPanelThread = agentPanelThreadIdRef.current
      if (prevPanelThread && prevPanelThread !== threadId) {
        const prevBinding = bindingsRef.current[prevPanelThread]
        if (prevBinding?.surface === 'agent-panel') {
          unbindThread(prevPanelThread)
        }
      }

      bindThread({ threadId, surface: 'agent-panel' })
      focusAgentPanel(threadId)
      setLastAction(`Opened in agent panel: ${resolveTitle(threadId)}`)
    },
    [bindThread, focusAgentPanel, resolveTitle, unbindThread, captureSessionSnapshot],
  )

  /** Record editor-tab host when layout opens a chat tab outside openInEditorTab (URL sync, etc.). */
  const ensureEditorBinding = useCallback((threadId: string) => {
    if (!threadId || threadId.startsWith('new-')) return
    const tabId = chatConversationTabId(threadId)
    const existing = bindingsRef.current[threadId]
    if (existing?.surface === 'editor' && existing.tabId === tabId) return

    if (agentPanelThreadIdRef.current === threadId) {
      agentPanelThreadIdRef.current = null
      setAgentPanelThreadId(null)
    }
    bindThread({ threadId, surface: 'editor', tabId })
  }, [bindThread])

  /**
   * Re-open the last agent-panel chat after idle / empty panel (session only).
   * Empty blank `new-*` drafts must not block restore (Welcome / New chat trap) when called
   * from mount / welcome-binder - but composer focus must not invoke this while a thread
   * (including intentional `new-*`) is already hosted.
   * Returns the restored (or already-hosted real) thread id, or null if nothing to show.
   */
  const restoreLastAgentPanelThread = useCallback((
    reason = 'unspecified',
    options?: RestoreLastAgentPanelOptions,
  ): string | null => {
    if (ingestionBootstrapThreadIdRef.current) {
      logChatSurfaceRestore({
        reason,
        from: agentPanelThreadIdRef.current,
        to: null,
        stored: readLastAgentPanelThreadId(),
        outcome: 'noop',
        detail: 'ingestion-bootstrap',
      })
      return null
    }

    const current = agentPanelThreadIdRef.current
    // Already showing a real conversation - return its id (not null).
    // Callers (welcome-composer binder) treat null as "create new chat"; returning null
    // here while the ref is already bound caused a race: focus restored EE Conf, React had
    // not re-rendered hostedThreadId yet, typing called createNewChat and unbound the real thread.
    if (current && !current.startsWith('new-')) {
      logChatSurfaceRestore({
        reason,
        from: current,
        to: current,
        stored: readLastAgentPanelThreadId(),
        outcome: 'keep-current',
      })
      return current
    }

    const stored = readLastAgentPanelThreadId()
    hydrateLastAgentPanelThreadMeta()
    if (shouldBlockSuppressedAutoRestore(reason, stored)) {
      logChatSurfaceRestore({
        reason,
        from: current,
        to: null,
        stored,
        outcome: 'noop',
        detail: 'auto-restore-suppressed',
      })
      return current?.startsWith('new-') ? current : null
    }
    if (!stored || stored === current) {
      logChatSurfaceRestore({
        reason,
        from: current,
        to: null,
        stored,
        outcome: 'noop',
        detail: !stored ? 'no-stored-last-thread' : 'stored-equals-current',
      })
      return null
    }
    // Welcome-composer typing into an empty panel: prefer reopening the last real
    // thread even if it is currently hosted in an editor tab (otherwise binder
    // creates a competing new-* and the prior chat looks "lost").
    const welcomeComposer =
      reason === 'welcome-composer' || reason === 'welcome-composer-retry'
    if (bindingsRef.current[stored]?.surface === 'editor' && !welcomeComposer) {
      logChatSurfaceRestore({
        reason,
        from: current,
        to: null,
        stored,
        outcome: 'noop',
        detail: 'stored-hosted-in-editor',
      })
      return null
    }

    const blankId =
      (current?.startsWith('new-') ? current : null) || getBlankNewChatId()
    if (blankId?.startsWith('new-')) {
      // Typed / attached draft - keep it.
      if (!isEmptyConversationDraft(getDraft(blankId))) {
        logChatSurfaceRestore({
          reason,
          from: current,
          to: blankId,
          stored,
          outcome: 'keep-blank',
          detail: 'non-empty-draft',
        })
        return null
      }
      // Intentional New chat (+ / Cmd+N / createNewChat). Do not steal it back to the
      // last real thread - that made the agent-panel + button flash and reopen the prior chat.
      if (activeNewChatIdRef.current === blankId) {
        logChatSurfaceRestore({
          reason,
          from: current,
          to: blankId,
          stored,
          outcome: 'keep-blank',
          detail: 'active-new-chat',
        })
        return blankId
      }
      if (options?.allowSwitch === false) {
        logChatSurfaceRestore({
          reason,
          from: current,
          to: null,
          stored,
          outcome: 'noop',
          detail: 'allow-switch-false',
        })
        return current?.startsWith('new-') ? current : null
      }
      // Orphan empty blank (no active new-chat guard) - discard and restore last real thread.
      if (agentPanelThreadIdRef.current === blankId) {
        agentPanelThreadIdRef.current = null
        setAgentPanelThreadId(null)
      }
      unbindThread(blankId)
      setDrafts((prev) => {
        if (!(blankId in prev)) return prev
        const next = { ...prev }
        delete next[blankId]
        return next
      })
    }

    if (options?.allowSwitch === false) {
      logChatSurfaceRestore({
        reason,
        from: current,
        to: null,
        stored,
        outcome: 'noop',
        detail: 'allow-switch-false',
      })
      return current?.startsWith('new-') ? current : null
    }

    if (
      !canRestoreAgentPanelThread(
        peekThreadWorkspaceMeta(stored),
        options?.activeWorkspaceIds,
      )
    ) {
      const metaUnknown = !peekThreadWorkspaceMeta(stored)
      const narrowed = Boolean(options?.activeWorkspaceIds?.length)
      if (metaUnknown && narrowed) {
        if (restoreLookupThreadIdRef.current !== stored) {
          restoreLookupThreadIdRef.current = stored
          void getThread(stored)
            .then((thread) => {
              rememberThreadWorkspaceMeta(thread)
              persistAgentPanelLastThreadMeta({
                threadId: thread.id,
                taggedOrganizationIds: thread.taggedOrganizationIds ?? [],
                workspaceId: thread.workspaceId ?? null,
                organizationId: thread.organizationId ?? null,
              })
              if (restoreLookupThreadIdRef.current !== stored) return
              restoreLookupThreadIdRef.current = null
              restoreLastAgentPanelThreadRef.current(`${reason}-after-meta`, options)
            })
            .catch((err) => {
              const status = (err as { status?: number })?.status
              // Ghost last-thread (cloud id on local Docker, or deleted). Stop retrying.
              if (status === 404 || status === 403) {
                clearLastAgentPanelThreadId(stored)
              }
              if (restoreLookupThreadIdRef.current === stored) {
                restoreLookupThreadIdRef.current = null
              }
            })
        }
        logChatSurfaceRestore({
          reason,
          from: current,
          to: null,
          stored,
          outcome: 'noop',
          detail: 'meta-lookup',
        })
        return current?.startsWith('new-') ? current : null
      }
      logChatSurfaceRestore({
        reason,
        from: current,
        to: null,
        stored,
        outcome: 'noop',
        detail: 'workspace-mismatch',
      })
      return current?.startsWith('new-') ? current : null
    }

    openInAgentPanel(stored)
    setLastAction(`Restored agent panel chat: ${resolveTitle(stored)}`)
    logChatSurfaceRestore({
      reason,
      from: current,
      to: stored,
      stored,
      outcome: 'switched',
    })
    return stored
  }, [getBlankNewChatId, getDraft, openInAgentPanel, resolveTitle, unbindThread])

  restoreLastAgentPanelThreadRef.current = restoreLastAgentPanelThread

  const isAgentPanelRestoreLookupPending = useCallback(
    () => restoreLookupThreadIdRef.current !== null,
    [],
  )

  const forgetAgentPanelThread = useCallback((threadId: string) => {
    clearLastAgentPanelThreadId(threadId)
  }, [])

  /**
   * Drop the hosted agent-panel thread and show Welcome.
   * Last-thread stays in sessionStorage, but automatic restore of that id is
   * suppressed until the user opens it (History / openThread).
   */
  const releaseAgentPanelToWelcome = useCallback((reason = 'workspace-mismatch') => {
    const current = agentPanelThreadIdRef.current
    if (!current || current.startsWith('new-')) return
    persistLastAgentPanelThreadId(current)
    suppressAgentPanelAutoRestore(current)
    unbindThread(current)
    agentPanelThreadIdRef.current = null
    setAgentPanelThreadId(null)
    setFocusedThreadId((id) => (id === current ? null : id))
    logChatSurfaceRestore({
      reason,
      from: current,
      to: null,
      stored: current,
      outcome: 'switched',
      detail: 'workspace-mismatch',
    })
  }, [unbindThread])

  /** Open thread in editor or agent panel per preference and options. */
  const openThread = useCallback(
    (threadId: string, options?: OpenConversationOptions) => {
      clearAgentPanelAutoRestoreSuppress(threadId)
      const useEditor =
        (openInEditor || options?.forceEditor || options?.openInNewTab)
        && !options?.forceAgentPanel
      if (options?.forceAgentPanel || !useEditor) {
        openInAgentPanel(threadId)
        return
      }
      openInEditorTab(threadId, { openInNewTab: options?.openInNewTab, title: options?.title })
    },
    [openInEditor, openInAgentPanel, openInEditorTab],
  )

  /** Move thread host to the agent panel. */
  const moveToAgentPanel = useCallback(
    (threadId: string) => openInAgentPanel(threadId),
    [openInAgentPanel],
  )

  /** Move thread host to an editor tab. */
  const moveToEditorTab = useCallback(
    (
      threadId: string,
      options?: Pick<OpenConversationOptions, 'openInNewTab' | 'title'>,
    ) => openInEditorTab(threadId, { ...options, openInNewTab: options?.openInNewTab ?? true }),
    [openInEditorTab],
  )

  /** Unbind registry state when a chat editor tab closes. */
  const closeEditorTab = useCallback(
    (tabId: string) => {
      const entry = Object.entries(bindingsRef.current).find(([, b]) => b.tabId === tabId)
      if (entry) {
        const [threadId] = entry
        unbindThread(threadId)
        if (activeNewChatIdRef.current === threadId) {
          setActiveNewChatId(null)
          activeNewChatIdRef.current = null
        }
      }
      setLastAction(`Closed editor tab ${tabId}`)
    },
    [unbindThread],
  )

  /** Toggle pin on the chat-conversation editor tab. */
  const togglePinEditorTab = useCallback((threadId: string) => {
    window.dispatchEvent(
      new CustomEvent('toggle-pin-chat-conversation-tab', { detail: { threadId } }),
    )
    setLastAction(`Toggled pin on chat tab ${threadId}`)
  }, [])

  /** Update cached title and rename open chat editor tab. */
  const updateTabTitle = useCallback((threadId: string, title: string) => {
    registerThreadTitle(threadId, title)
    window.dispatchEvent(
      new CustomEvent('rename-chat-conversation-tab', { detail: { threadId, title } }),
    )
    setLastAction(`Renamed tab: ${title}`)
  }, [registerThreadTitle])

  /** Release one-empty-chat guard after first message is sent. */
  const markChatStarted = useCallback(
    (threadId: string) => {
      if (activeNewChatIdRef.current === threadId) {
        setActiveNewChatId(null)
        activeNewChatIdRef.current = null
      }
      setLastAction(`Chat started: ${resolveTitle(threadId)}`)
    },
    [resolveTitle],
  )

  /** Re-key registry state when placeholder thread id becomes real. */
  const migrateThreadId = useCallback(
    (fromThreadId: string, toThreadId: string, title?: string) => {
      if (fromThreadId === toThreadId) return
      if (fromThreadId.startsWith('new-')) {
        rekeyHeldUserTurn(fromThreadId, toThreadId)
        markPromotedFromNewThread(toThreadId)
      }

      const binding = bindingsRef.current[fromThreadId]
      const draft = drafts[fromThreadId]
      const oldTitle = threadTitlesRef.current[fromThreadId]

      if (draft) {
        setDrafts((prev) => {
          const next = { ...prev, [toThreadId]: draft }
          delete next[fromThreadId]
          return next
        })
      }

      if (oldTitle || title) {
        registerThreadTitle(toThreadId, title ?? oldTitle ?? defaultTitleForThread(toThreadId))
      }

      if (binding) {
        const newTabId = chatConversationTabId(toThreadId)
        bindThread({ ...binding, threadId: toThreadId, tabId: newTabId })
        unbindThread(fromThreadId)
        window.dispatchEvent(
          new CustomEvent('migrate-chat-conversation-tab', {
            detail: { fromThreadId, toThreadId, title: title ?? oldTitle },
          }),
        )
      }

      if (activeNewChatIdRef.current === fromThreadId) {
        setActiveNewChatId(null)
        activeNewChatIdRef.current = null
      }
      setAgentPanelThreadId((current) => (current === fromThreadId ? toThreadId : current))
      persistLastAgentPanelThreadId(toThreadId)
    },
    [bindThread, drafts, registerThreadTitle, unbindThread],
  )

  /** Create or focus a single empty new chat (no duplicate welcome screens). */
  const createNewChat = useCallback(
    (options?: Pick<OpenConversationOptions, 'openInNewTab' | 'forceAgentPanel'> & {
      contextDocumentId?: string
      contextDocumentTitle?: string
      threadId?: string
      threadTitle?: string
      /** When true, always allocate a new thread (e.g. Basecamp todo notes). */
      preferNew?: boolean
    }) => {
      const useEditor = openInEditor && !options?.forceAgentPanel
      const panelHostsPersisted =
        Boolean(agentPanelThreadIdRef.current)
        && !agentPanelThreadIdRef.current!.startsWith('new-')
      const existingBlankId =
        options?.preferNew || options?.threadId
          ? null
          : panelHostsPersisted
            ? null
            : getBlankNewChatId()
      if (existingBlankId) {
        if (options?.contextDocumentId || options?.contextDocumentTitle || options?.threadTitle) {
          setDrafts((prev) => ({
            ...prev,
            [existingBlankId]: {
              ...(prev[existingBlankId] ?? { text: '', references: [], images: [] }),
              contextDocumentId: options?.contextDocumentId ?? prev[existingBlankId]?.contextDocumentId,
              contextDocumentTitle:
                options?.contextDocumentTitle ?? prev[existingBlankId]?.contextDocumentTitle,
            },
          }))
          if (options?.threadTitle) {
            registerThreadTitle(existingBlankId, options.threadTitle)
          }
        }
        if (useEditor) {
          openInEditorTab(existingBlankId, {
            openInNewTab: options?.openInNewTab ?? false,
            title: 'New chat',
          })
        } else {
          openInAgentPanel(existingBlankId)
        }
        focusChatComposer(existingBlankId)
        setLastAction('Focused existing new chat (no duplicate)')
        return existingBlankId
      }

      const threadId = options?.threadId ?? `new-${Date.now()}`
      setDrafts((prev) => ({
        ...prev,
        [threadId]: {
          text: '',
          references: [],
          images: [],
          contextDocumentId: options?.contextDocumentId,
          contextDocumentTitle: options?.contextDocumentTitle,
        },
      }))
      registerThreadTitle(threadId, options?.threadTitle ?? 'New chat')
      setActiveNewChatId(threadId)
      activeNewChatIdRef.current = threadId

      if (useEditor) {
        if (agentPanelThreadIdRef.current?.startsWith('new-')) {
          unbindThread(agentPanelThreadIdRef.current)
        }
        agentPanelThreadIdRef.current = null
        setAgentPanelThreadId(null)
        openInEditorTab(threadId, { openInNewTab: options?.openInNewTab ?? true, title: 'New chat' })
      } else {
        const prevHosted = agentPanelThreadIdRef.current
        if (prevHosted && prevHosted !== threadId) {
          // Keep last-real-thread restorable when an empty new-* replaces the panel.
          if (!prevHosted.startsWith('new-')) {
            persistLastAgentPanelThreadId(prevHosted)
          }
          unbindThread(prevHosted)
          if (prevHosted.startsWith('new-')) {
            window.dispatchEvent(
              new CustomEvent('close-chat-conversation-tab', { detail: { threadId: prevHosted } }),
            )
          }
        }
        for (const boundId of Object.keys(bindingsRef.current)) {
          if (boundId.startsWith('new-') && boundId !== threadId) {
            window.dispatchEvent(
              new CustomEvent('close-chat-conversation-tab', { detail: { threadId: boundId } }),
            )
            unbindThread(boundId)
          }
        }
        bindThread({ threadId, surface: 'agent-panel' })
        agentPanelThreadIdRef.current = threadId
        setAgentPanelThreadId(threadId)
        setAgentPanelExpanded(true)
      }
      focusChatComposer(threadId)
      setLastAction('Created new chat')
      return threadId
    },
    [
      bindThread,
      getBlankNewChatId,
      openInEditor,
      openInAgentPanel,
      openInEditorTab,
      registerThreadTitle,
      unbindThread,
    ],
  )

  /** Command palette / Basecamp ingest: agent panel only, scoped conversation bootstrap. */
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<OpenIngestionInAgentPanelDetail>).detail
      if (!detail?.threadId || !detail?.conversationId) return

      ingestionBootstrapThreadIdRef.current = detail.threadId
      if (detail.title) {
        registerThreadTitle(detail.threadId, detail.title)
      }
      openInAgentPanel(detail.threadId)
      setFocusedThreadId(detail.threadId)
      refreshChatsSidebar()

      window.dispatchEvent(
        new CustomEvent('open-chat-conversation', {
          detail: {
            conversationId: detail.conversationId,
            targetThreadId: detail.threadId,
            targetSurface: 'agent-panel',
            autoScroll: true,
          },
        }),
      )
    }

    window.addEventListener(OPEN_INGESTION_IN_AGENT_PANEL, handler)
    return () => window.removeEventListener(OPEN_INGESTION_IN_AGENT_PANEL, handler)
  }, [openInAgentPanel, registerThreadTitle, setFocusedThreadId])

  return {
    openInEditor,
    setOpenInEditor,
    agentPanelThreadId,
    setAgentPanelThreadId,
    agentPanelExpanded,
    setAgentPanelExpanded,
    activeNewChatId,
    getBlankNewChatId,
    getDraft,
    setDraft,
    getBinding,
    openThread,
    openConversation: openThread,
    moveToAgentPanel,
    moveToEditorTab,
    closeEditorTab,
    togglePinEditorTab,
    updateTabTitle,
    registerThreadTitle,
    markChatStarted,
    createNewChat,
    migrateThreadId,
    bindings,
    streamingConversationId,
    setStreamingConversationId,
    streamingThreadId,
    setStreamingThreadId,
    focusedThreadId,
    setFocusedThreadId,
    isThreadOpen,
    isThreadActive,
    resolveTitle,
    lastAction,
    restoreLastAgentPanelThread,
    isAgentPanelRestoreLookupPending,
    peekLastAgentPanelThreadId: readLastAgentPanelThreadId,
    forgetAgentPanelThread,
    releaseAgentPanelToWelcome,
    endIngestionBootstrap,
    ensureEditorBinding,
    setSessionExporter,
    clearSessionExporter,
    takeSessionSnapshot,
    captureSessionSnapshot,
  }
}
